import React, { useState } from 'react';
import { View, Text, StyleSheet, SafeAreaView, TouchableOpacity, ActivityIndicator, Platform, Alert, Dimensions } from 'react-native';
import { useRouter } from 'expo-router';
import { Colors, SIZES } from '../constants/theme';
import { Button } from '../components/Button';
import { Ionicons } from '@expo/vector-icons';
import { useAppStore } from '../store/useAppStore';
import { biometricService } from '../services/biometricService';
import { storageService, resizeToAspect } from '../services/storageService';
import ScreenBackground from '../components/ScreenBackground';
import { CameraView, useCameraPermissions } from 'expo-camera';
import { firestoreService } from '../services/firestoreService';

const { width, height } = Dimensions.get('window');

export default function KycBiometricsScreen() {
  const router = useRouter();
  const role = useAppStore((state) => state.userRole);
  // `setVerified` se quitó a propósito: la verificación ya NO la decide esta
  // pantalla. El flag real vive en Firestore y solo lo escribe la Cloud
  // Function `reviewKycSubmission` cuando un humano aprueba la solicitud.
  const kycDocumentUri = useAppStore((state) => state.kycDocumentUri);
  const kycDocumentBase64 = useAppStore((state) => state.kycDocumentBase64);

  const [step, setStep] = useState(0);
  const [isScanning, setIsScanning] = useState(false);
  const [selfieUri, setSelfieUri] = useState<string | null>(null);
  const [selfieBase64, setSelfieBase64] = useState<string | null>(null);
  
  const [permission, requestPermission] = useCameraPermissions();
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraRef, setCameraRef] = useState<any>(null);
  // Candado del obturador: `takePictureAsync` tarda casi un segundo y el botón
  // no da ninguna señal mientras tanto. Sin esto, dos toques seguidos disparan
  // DOS envíos de KYC y el segundo muere con `already-exists`, que en pantalla
  // se lee como si el primero hubiera fallado.
  const [capturando, setCapturando] = useState(false);

  // Estos textos describen lo que REALMENTE pasa. Antes decían "Analizando
  // biometría con AWS..." e "Identidad verificada exitosamente": las dos son
  // falsas desde que se quitó Rekognition. No hay ningún análisis automático —
  // las fotos viajan a Firebase Storage y las compara una persona— y al
  // terminar el usuario queda PENDIENTE, no verificado (el sello lo escribe la
  // Cloud Function `reviewKycSubmission` cuando el admin aprueba).
  // Para Google Play describir mal el tratamiento de datos biométricos es
  // infracción por partida doble: declaración falsa + Seguridad de los datos
  // que no cuadra con la política de privacidad.
  const instructions = [
    "Presiona 'Abrir Cámara' cuando estés listo.",
    'Enviando tus documentos para revisión…',
    'Documentos enviados. Los revisa una persona de nuestro equipo.',
  ];

  const handleStartScan = async () => {
    // QA brief: flujo 100% nativo (expo-camera + expo-local-authentication).
    // Cero DOM, cero navigator.mediaDevices, cero getUserMedia. El permiso
    // de cámara se pide ANTES de cualquier otra acción para que el user no
    // vea pantallas raras a la mitad del flujo.
    //
    // Cero regresión: el guard biométrico (Face ID/Huella) y el guard de
    // kycDocumentUri se mantienen idénticos.

    // 1) Pre-check del documento (sin cambios).
    if (!kycDocumentUri) {
      Alert.alert(
        'Falta la cédula',
        'Necesitamos la foto del FRENTE de la cédula antes de tomar la selfie. Toca Atrás y completa ese paso.'
      );
      return;
    }

    // 2) Permiso de cámara nativo — `useCameraPermissions` de expo-camera.
    //    Si está denegado, pedimos UNA vez con `requestPermission()`.
    //    Si el user lo niega explícitamente, mostramos Alert con la ruta
    //    para habilitarlo manualmente. No empujamos al user a otra
    //    pantalla — solo lo informamos.
    if (!permission?.granted) {
      const result = await requestPermission();
      if (!result.granted) {
        Alert.alert(
          'Permiso de cámara denegado',
          // Sin "prueba de vida": no hay detección automática de nada, es una
          // selfie que revisa una persona.
          'Go Safely necesita acceso a la cámara para tomar la selfie con la que verificamos tu identidad. Habilita el permiso desde Configuración del sistema → Aplicaciones → Go Safely → Permisos → Cámara, y vuelve a intentar.'
        );
        return;
      }
    }

    // 3) Guard biométrico OS (Face ID / Huella / PIN).
    //    biometricService usa `expo-local-authentication` (nativo) —
    //    confirmado en services/biometricService.ts. NO usa Web Auth API.
    //
    //    SE DISTINGUE "el usuario se negó" de "este teléfono no puede
    //    verificar". Antes los dos casos devolvían false y los dos bloqueaban:
    //    en un teléfono sin bloqueo de pantalla —el emulador limpio con el que
    //    revisa Google Play— no había forma de pasar de esta pantalla, y ese
    //    callejón sin salida es rechazo directo. Si el aparato no puede
    //    verificar, se sigue y queda constancia en el log; el portero de verdad
    //    es la revisión humana de la cédula, no el lector de huellas.
    const guardia = await biometricService.verificarIdentidadDelDispositivo(
      'Confirma que eres tú para vincular este teléfono a tu cuenta Go Safely'
    );
    if (guardia.estado === 'RECHAZADO') {
      Alert.alert(
        'Seguridad Requerida',
        'Debes autorizar con Face ID, Huella o PIN para vincular este dispositivo a tu cuenta.'
      );
      return;
    }
    if (guardia.estado === 'SIN_BLOQUEO_DE_PANTALLA') {
      console.warn('[KYC] se continúa sin verificación del aparato:', guardia.motivo);
    }

    // 4) Abre el CameraView nativo (expo-camera).
    setCameraActive(true);
  };

  const takeSelfie = async () => {
    if (capturando) return;

    // SIN ESTE AVISO EL BOTÓN NO HACÍA NADA, EN SILENCIO.
    // Antes todo el cuerpo estaba dentro de `if (cameraRef)`: si la referencia
    // aún no había llegado (el primer render de CameraView), pulsar el
    // obturador no producía absolutamente nada — ni foto, ni error, ni cambio
    // en pantalla. Y si `takePictureAsync` fallaba, el `catch` solo escribía en
    // la consola: la cámara se quedaba abierta y el usuario seguía pulsando un
    // botón muerto. "Botones que no responden" es, con nombre y apellido, el
    // motivo vc13 por el que Google ya rechazó esta app.
    if (!cameraRef) {
      Alert.alert(
        'La cámara aún no está lista',
        'Espera un segundo a que aparezca tu rostro en pantalla y vuelve a pulsar el botón.'
      );
      return;
    }

    setCapturando(true);
    try {
      const photo = await cameraRef.takePictureAsync({
        quality: 0.8,
        // base64 nativo de expo-camera. Se sigue pidiendo porque es la prueba
        // de que la cámara devolvió una imagen legible (el pre-check de más
        // abajo lo comprueba); la subida ya NO usa este base64 crudo, ver el
        // ajuste de tamaño de aquí mismo.
        base64: true,
      });

      if (!photo?.uri) {
        throw new Error('la cámara no devolvió ninguna foto');
      }

      setCameraActive(false);

      // ═══════════════════════════════════════════════════════════════════
      // AJUSTE DE TAMAÑO — SIN ESTO LA PRIMERA PANTALLA ERA UN CALLEJÓN SIN
      // SALIDA.
      //
      // La selfie sube a `user_avatars/{uid}/` y esa regla de Storage tiene un
      // tope DURO de 3 MB (`maxSize(3)` en storage.rules). `takePictureAsync`
      // captura a la resolución completa del sensor: en un móvil moderno
      // (48 MP y más) una foto a calidad 0.8 pasa de 3 MB con facilidad. La
      // regla la rechaza SIEMPRE, con el mismo error para todos los intentos,
      // y el usuario se queda repitiendo la selfie sin poder avanzar del
      // primer paso del registro. Un revisor de Play con un teléfono nuevo
      // habría visto exactamente eso.
      //
      // Se reutiliza `resizeToAspect` de storageService (el mismo ajustador de
      // vehículos y restaurantes) en vez de escribir otro. Aspecto 9:16 a lo
      // ancho 1080 significa en la práctica "reduce el ancho a 1080 y NO
      // recortes": el recorte solo entra si la foto es más alta que 1920, y
      // ninguna foto de móvil (4:3 o 16:9) lo es tras reducirla. Recortar sí
      // sería peligroso aquí: la cara va centrada en el óvalo y un corte le
      // quitaría la frente justo en la foto que alguien tiene que comparar
      // contra la cédula. Resultado típico: 1080x1440 y unos 300 KB.
      //
      // Si el ajuste falla, `resizeToAspect` devuelve la original y el envío
      // continúa: en ese caso el mensaje de error de abajo ya distingue "pesa
      // demasiado" de "no hay internet".
      // ═══════════════════════════════════════════════════════════════════
      const selfieAjustada = await resizeToAspect(photo.uri, 1080, 9, 16);

      setSelfieUri(selfieAjustada);
      setSelfieBase64(photo.base64 ?? null);

      enviarKycARevision(selfieAjustada, photo.base64 ?? null);
    } catch (error: any) {
      console.warn('[KYC] falló la captura de la selfie:', error?.message);
      // La cámara se CIERRA. Dejarla abierta tras un fallo es lo que hacía
      // parecer que la app se colgó.
      setCameraActive(false);
      Alert.alert(
        'No pudimos tomar la foto',
        'La cámara no llegó a guardar la imagen. Cierra otras apps que estén usando la cámara y vuelve a intentarlo desde "Abrir Cámara".'
      );
    } finally {
      setCapturando(false);
    }
  };

  const enviarKycARevision = async (capturedSelfieUri: string, capturedSelfieBase64: string | null) => {
    setIsScanning(true);
    setStep(1); // "Enviando tus documentos para revisión…"

    // PRE-CHECK: si el usuario llegó aquí sin haber subido la cédula,
    // kycDocumentUri viene null. En móvil también pedimos kycDocumentBase64,
    // que se llena al tomar la foto del Frente. Sin uno o el otro paramos.
    if (!kycDocumentUri || String(kycDocumentUri).length === 0) {
      Alert.alert(
        'Falta la cédula',
        'Primero debes subir la foto del FRENTE de tu cédula desde la pantalla anterior.'
      );
      resetState();
      return;
    }
    if (Platform.OS !== 'web' && (!kycDocumentBase64 || !capturedSelfieBase64)) {
      Alert.alert(
        'Foto incompleta',
        'No pudimos leer la foto. Vuelve a tomar la cédula y reintenta la selfie.'
      );
      resetState();
      return;
    }

    try {
      // ═══════════════════════════════════════════════════════════════════
      // ENVÍO A REVISIÓN MANUAL (antes: comparación facial en AWS Rekognition
      // detrás del backend de Railway).
      //
      // Railway fue BORRADO: devolvía 404 en todas sus rutas y, como no
      // responde JSON, el `response.json()` de abajo reventaba con
      // "JSON Parse error: Unexpected character: A". El usuario leía
      // "Verificación Fallida — las fotos no coinciden", culpando a su propia
      // foto cuando el servidor sencillamente no existía. Ese bucle sin
      // salida es el rechazo vc16 de Google Play ("app does not open or load").
      //
      // Ahora: las fotos van DIRECTO a Firebase Storage y la solicitud entra
      // en una cola que revisa una persona. Sin AWS, sin costo por
      // verificación, y con el fundador decidiendo quién opera — el mismo
      // modelo con el que arrancó InDriver.
      // ═══════════════════════════════════════════════════════════════════
      const uid = useAppStore.getState().userFirebaseUid;
      if (!uid) {
        Alert.alert(
          'Sesión no detectada',
          'Cierra la app y vuelve a iniciar sesión antes de verificar la identidad.'
        );
        resetState();
        return;
      }

      const esUrlDeStorage = (v: unknown) =>
        typeof v === 'string' && v.startsWith('https://firebasestorage.googleapis.com/');

      // 1) CÉDULA. `kyc-documents.tsx` ya la sube a Storage y guarda la URL.
      //    Si por el camino quedó un URI local, la subimos aquí.
      console.warn('[KYC] paso 1: asegurando la cédula en Storage…');
      const cedulaUrl = esUrlDeStorage(kycDocumentUri)
        ? (kycDocumentUri as string)
        : await storageService.uploadKYCDocument(
            uid,
            'cedula_frente',
            kycDocumentUri as string,
            kycDocumentBase64 ?? null
          );

      // 2) SELFIE. Sube a user_avatars/{uid}/ y de paso queda como avatar:
      //    es la foto que verán los choferes/pasajeros, y la que el admin
      //    compara contra la cédula.
      console.warn('[KYC] paso 2: subiendo la selfie…');
      // Se sube el URI de la foto YA AJUSTADA (ver `takeSelfie`), no el base64
      // crudo del sensor. Antes se armaba aquí un `data:image/jpeg;base64,…`
      // con la foto a resolución completa: eso es justo lo que hacía saltar el
      // tope de 3 MB de la regla de `user_avatars`. `uploadLocalUri` acepta el
      // `file://` que devuelve el ajustador y lo sube con `putFile`, que además
      // no tiene que cargar la imagen entera en memoria.
      const selfieUrl = await storageService.uploadProfilePhoto(uid, capturedSelfieUri);
      if (selfieUrl) {
        useAppStore.getState().setUserPhoto(selfieUrl);
        useAppStore.getState().setProfilePhotoTaken(true);
        // ⚠️ TAMBIÉN AL DOCUMENTO DEL PERFIL, no solo al store del teléfono.
        //
        // Antes la selfie quedaba en Storage, en la solicitud de KYC y en el
        // store local — y NADIE la escribía en `users/{uid}`. Al reinstalar o
        // volver a entrar, la restauración leía el documento, no encontraba
        // foto, y adoptaba el avatar de la cuenta de Google: en el caso real
        // del fundador, el LOGO de una empresa como cara del chofer. El
        // pasajero decide si montarse mirando esa foto.
        //
        // Se escriben los dos nombres porque históricamente hubo dos lectores:
        // `userPhoto` (el que escribe la aprobación del KYC y lee _layout) y
        // `profilePhoto` (el campo viejo de la foto manual).
        try {
          await firestoreService.updateUserProfile(uid, {
            userPhoto: selfieUrl,
            profilePhoto: selfieUrl,
          });
        } catch (e: any) {
          // No bloquea el KYC: la selfie ya viaja en la solicitud y la
          // aprobación del admin la escribirá de todos modos.
          console.warn('[KYC] no se pudo fijar la selfie como foto de perfil:', e?.message);
        }
      }

      // 3) SOLICITUD. La Cloud Function valida que ambas URLs pertenezcan a
      //    la carpeta de Storage de ESTE usuario — nadie puede enviar la
      //    cédula de otra persona.
      console.warn('[KYC] paso 3: enviando solicitud a revisión…');
      const { callSubmitKycForReview } = await import('../services/cloudFunctions');

      // ══════════════════════════════════════════════════════════════════
      // LA SOLICITUD IBA CON UNA SOLA FOTO
      // ══════════════════════════════════════════════════════════════════
      // Esto mandaba `{ cedula_frente: cedulaUrl }` y nada más. Pero el chofer
      // ya subió en `kyc-documents.tsx` la cédula por los dos lados, su
      // licencia, la matrícula, el seguro y cinco fotos del vehículo — todo
      // guardado en `users/{uid}.kycDocs`.
      //
      // Resultado medido el 2-oct-2026: en el panel solo se veían la cédula y
      // la selfie, o sea que se aprobaba a alguien para llevar pasajeros SIN
      // VER SU LICENCIA NI SU SEGURO.
      //
      // El servidor valida cada URL contra la carpeta de ESTE usuario y
      // descarta lo que no cuadre (`submitKycForReview`), así que mandar el
      // mapa entero no abre ningún hueco: lo que no sea suyo no entra.
      let documentos: Record<string, string> = { cedula_frente: cedulaUrl };
      try {
        const perfil = await firestoreService.getUserProfile(uid);
        const { leerKycDocs, urlDeDocumento } = await import('../utils/kycDocs');
        // EL CARNET DE LA DIRECTORA VIVE FUERA DE `kycDocs`.
        // `kyc-director.tsx` lo guarda en `users/{uid}.kycCarnetUrl`, campo de
        // primer nivel, asi que `leerKycDocs` no lo ve. Sin esta linea la
        // directora manda a revisar SOLO su selfie, y el carnet del colegio
        // —el unico documento que se le pide— no llega al panel.
        const sueltos: Record<string, any> = {};
        if ((perfil as any)?.kycCarnetUrl) sueltos.carnet_institucional = (perfil as any).kycCarnetUrl;
        for (const [clave, valor] of Object.entries({ ...sueltos, ...leerKycDocs(perfil) })) {
          const url = urlDeDocumento(valor);
          if (typeof url === 'string' && url.startsWith('https://')) documentos[clave] = url;
        }
      } catch (e: any) {
        // Si no se pueden leer, la solicitud sale igual con la cédula: es peor
        // dejar al chofer sin poder enviarla. El panel los lee del perfil de
        // todos modos (`listPendingKycSubmissions`).
        console.warn('[KYC] no se pudieron adjuntar los demás documentos:', e?.message);
      }

      await callSubmitKycForReview({
        documents: documentos,
        selfieUrl,
      });
      console.warn('[KYC] ✅ solicitud registrada en la cola');

      {
        // La selfie ya quedó subida y guardada como avatar en el paso 2.
        // Ya NO hay OCR de la cédula: el nombre real lo confirma la persona
        // que revisa, y lo escribe `reviewKycSubmission` al aprobar.
        setStep(2); // "Solicitud enviada a revisión"

        // IMPORTANTE: aquí NO se llama a setVerified(true).
        // Antes se marcaba al usuario como verificado apenas AWS devolvía
        // match. Ahora la decisión es de una persona, así que el usuario
        // queda PENDIENTE hasta que el admin apruebe. Marcarlo verificado
        // aquí sería mentirle — y además el flag real vive en Firestore,
        // donde solo lo escribe la Cloud Function: el store local se
        // sobreescribiría al siguiente arranque de todos modos.
        Alert.alert(
          'Verificación enviada',
          'Recibimos tu cédula y tu selfie. Un miembro del equipo las revisa y te avisamos por notificación — normalmente el mismo día.\n\nMientras tanto puedes seguir usando la app.'
        );

        setTimeout(() => {
          // TELÉFONO OBLIGATORIO — quien entró con Google llega sin número.
          // Sin él no podemos contactarlo para el viaje, así que sigue siendo
          // un paso obligatorio aunque la verificación esté pendiente.
          const phoneDigits = String(useAppStore.getState().userPhone ?? '').replace(/[^0-9]/g, '');
          const needsPhone = phoneDigits.length < 8;
          if (role === 'PASSENGER' || role === 'FOOD_DELIVERY') {
            if (needsPhone) { router.replace('/add-phone'); return; }
            if (role === 'FOOD_DELIVERY') {
              router.replace('/(tabs)/food');
            } else {
              router.replace('/(tabs)/map');
            }
          } else if (role === 'SCHOOL_DIRECTOR') {
            if (needsPhone) { router.replace('/add-phone'); return; }
            router.replace('/school-director-dashboard');
          } else if (role === 'RENT_CAR_OWNER') {
            if (needsPhone) { router.replace('/add-phone'); return; }
            router.replace('/(tabs)/dashboard');
          } else if (role === 'CAR_RENTER') {
            if (needsPhone) { router.replace('/add-phone'); return; }
            router.replace('/(tabs)/marketplace');
          } else {
            // FIX FLUJO DRIVER — Antes el chofer caía a /kyc-subscription:
            // una pantalla stub con inputs sin Stripe real, decía "30 días"
            // (lo correcto son 15), sin auto-formato de tarjeta, sin
            // botón de submit y sin opción de recargar billetera por
            // transferencia. Bloqueaba el flujo y obligaba al user a
            // cerrar la app.
            //
            // El chofer entra al mapa igual que el pasajero. Tampoco se le
            // marca como verificado aquí: `isDriverVerified` lo escribe
            // `reviewKycSubmission` al aprobar. Antes se ponía en true solo
            // en el store local y al reabrir la app el guard leía Firestore
            // (que decía false) y lo rebotaba al embudo de KYC otra vez.
            if (needsPhone) { router.replace('/add-phone'); return; }
            router.replace('/(tabs)/map');
          }
        }, 1500);
      }
    } catch (error: any) {
      console.warn('[KYC] ❌ ERROR:', error?.code, error?.message);
      const code = String(error?.code ?? '');
      const raw = String(error?.message ?? '');
      let msg: string;

      // Errores de la subida y de la Cloud Function, traducidos a lenguaje
      // humano y —lo importante— DISTINGUIENDO la causa: "tu foto pesa
      // demasiado" y "no hay internet" se arreglan de formas opuestas, y darle
      // el mismo texto a las dos deja al usuario repitiendo lo que no toca.
      //
      // ── FALLOS DE LA SUBIDA A STORAGE ───────────────────────────────────
      // Van PRIMERO porque son los que de verdad ocurren en la calle, y porque
      // el genérico de abajo ("intenta de nuevo en un momento") invitaba a
      // repetir eternamente algo que iba a fallar siempre.
      //
      // `storage/unauthorized` es la regla diciendo que no. Con la sesión ya
      // comprobada arriba y un JPEG ajustado, lo que queda es el TAMAÑO: la
      // regla de `user_avatars` corta en 3 MB. Se nombra el peso en el mensaje
      // para que el usuario no crea que es su conexión.
      if (code.includes('storage/unauthorized') || raw.includes('storage/unauthorized')) {
        msg =
          'El servidor rechazó la foto, casi siempre porque pesa demasiado (el tope es 3 MB). ' +
          'Vuelve a tomar la selfie: la reducimos antes de subirla. Si sigue pasando, cierra la ' +
          'app, entra de nuevo e inténtalo.';
      } else if (code.includes('storage/quota-exceeded') || raw.includes('quota')) {
        msg =
          'Se nos llenó el espacio de almacenamiento y no pudimos guardar tu foto. No es culpa ' +
          'tuya ni de tu teléfono: escríbenos por WhatsApp y lo resolvemos.';
      } else if (
        code.includes('storage/retry-limit-exceeded') ||
        code.includes('storage/canceled') ||
        raw.toLowerCase().includes('retry limit') ||
        raw.toLowerCase().includes('timeout')
      ) {
        msg =
          'La foto no terminó de subir por la conexión. Conéctate a Wi-Fi o busca mejor señal e ' +
          'intenta de nuevo. Tu foto está bien; es la red.';
      } else if (code.includes('already-exists')) {
        msg = 'Ya tienes una verificación en revisión. Te avisamos apenas la revisemos.';
      } else if (code.includes('failed-precondition') && raw.includes('ya está verificada')) {
        msg = 'Tu identidad ya está verificada. No necesitas volver a enviarla.';
      } else if (code.includes('invalid-argument')) {
        msg = 'Alguna de las fotos no se subió bien. Vuelve a tomar la cédula y la selfie e intenta de nuevo.';
      } else if (code.includes('unauthenticated')) {
        msg = 'Tu sesión expiró. Cierra la app, vuelve a entrar e intenta de nuevo.';
      } else if (raw.toLowerCase().includes('network') || raw.toLowerCase().includes('unavailable')) {
        msg = 'Sin conexión estable. Verifica tu Wi-Fi o datos móviles e intenta de nuevo.';
      } else {
        // NUNCA imprimimos error.message crudo: antes salía
        // "JSON Parse error: Unexpected character: A" y el usuario no
        // entendía nada. El detalle técnico queda en el log, no en pantalla.
        msg = 'No pudimos enviar tu verificación. Intenta de nuevo en un momento.';
      }
      Alert.alert('Verificación no enviada', msg);
      resetState();
    }
  };

  const resetState = () => {
    setIsScanning(false);
    setStep(0);
    setSelfieUri(null);
  };

  if (cameraActive) {
    // CÁMARA EN VIVO con OVERLAY de óvalo guía.
    // ANTES: usábamos un "cutout" con borderWidth gigante que pretendía recortar
    // un óvalo transparente sobre la cámara. En Android ese truco se renderizaba
    // como una pantalla AZUL sólida sin mostrar la cámara — bug crítico.
    // AHORA: composición simple con 4 paneles oscuros que enmarcan un óvalo
    // visible donde el usuario ve su rostro contra la cámara real.
    const OVAL_WIDTH_RATIO = 0.72;       // 72% del ancho de pantalla
    const OVAL_HEIGHT_RATIO = 0.45;      // 45% del alto de pantalla
    const ovalW = width * OVAL_WIDTH_RATIO;
    const ovalH = height * OVAL_HEIGHT_RATIO;
    const ovalLeft = (width - ovalW) / 2;
    const ovalTop = (height - ovalH) / 2.4; // ligeramente arriba del centro
    const dimColor = 'rgba(10, 37, 64, 0.78)';

    return (
      <View style={styles.cameraScreen}>
        <CameraView
          style={StyleSheet.absoluteFill}
          facing="front"
          ref={(ref) => setCameraRef(ref)}
        />

        {/* 4 paneles oscuros alrededor del óvalo — la cámara queda visible
            solo dentro del óvalo central, sin trucos de borderWidth. */}
        <View pointerEvents="none" style={{ position: 'absolute', top: 0, left: 0, right: 0, height: ovalTop, backgroundColor: dimColor }} />
        <View pointerEvents="none" style={{ position: 'absolute', top: ovalTop + ovalH, left: 0, right: 0, bottom: 0, backgroundColor: dimColor }} />
        <View pointerEvents="none" style={{ position: 'absolute', top: ovalTop, left: 0, width: ovalLeft, height: ovalH, backgroundColor: dimColor }} />
        <View pointerEvents="none" style={{ position: 'absolute', top: ovalTop, right: 0, width: ovalLeft, height: ovalH, backgroundColor: dimColor }} />

        {/* Marco visible del óvalo (decorativo) */}
        <View
          pointerEvents="none"
          style={{
            position: 'absolute',
            top: ovalTop,
            left: ovalLeft,
            width: ovalW,
            height: ovalH,
            borderRadius: ovalW / 2,
            borderWidth: 3,
            borderColor: '#FFF',
            shadowColor: '#FFF',
            shadowOpacity: 0.6,
            shadowRadius: 8,
          }}
        />

        {/* Instrucción + controles */}
        <View style={styles.cameraOverlayContent}>
          <Text style={styles.cameraInstructionText}>
            {capturando ? 'Quieto un momento…' : 'Ubica tu rostro dentro del círculo'}
          </Text>
          <View style={styles.cameraControls}>
            {/* El obturador se APAGA mientras trabaja y se ve apagado. Antes no
                cambiaba nada al pulsarlo durante el segundo que tarda la
                captura, así que el usuario volvía a pulsar creyendo que no
                había funcionado. */}
            <TouchableOpacity
              style={[styles.shutterButton, capturando && styles.shutterButtonDisabled]}
              onPress={takeSelfie}
              disabled={capturando}
            >
              {capturando ? (
                <ActivityIndicator color="#FFF" />
              ) : (
                <View style={styles.shutterButtonInner} />
              )}
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.closeCameraButton}
              onPress={() => setCameraActive(false)}
              disabled={capturando}
            >
              <Text style={[styles.closeCameraText, capturando && { opacity: 0.5 }]}>Cancelar</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    );
  }

  return (
    <ScreenBackground>
      <SafeAreaView style={styles.safeArea}>
      <TouchableOpacity onPress={() => {
        if (router.canGoBack()) router.back();
        else router.replace('/');
      }} style={styles.backButton}>
        <Ionicons name="arrow-back" size={24} color={Colors.white} />
      </TouchableOpacity>
      <View style={styles.container}>
        <View style={styles.header}>
          {/* "Prueba de Vida (Face ID)" prometía una detección automática que
              no existe: no hay liveness, ni reconocimiento facial, ni ningún
              modelo mirando la foto. Es una selfie que compara un humano contra
              la cédula. El título y el texto lo dicen tal cual, y avisan de que
              la foto se guarda — que es lo que el revisor de Play contrasta
              contra el formulario de Seguridad de los datos. */}
          <Text style={styles.title}>Selfie de verificación</Text>
          <Text style={styles.subtitle}>
            Tómate una selfie con buena iluminación. Una persona de nuestro equipo la comparará
            con la foto de tu cédula. La guardamos mientras tu cuenta exista y pasa a ser tu foto
            de perfil, para que quien viaje contigo sepa a quién se encuentra.
          </Text>
        </View>

        <View style={styles.previewContainer}>
          {/* El paso intermedio era 👀 ("te estamos mirando"), que reforzaba la
              idea del análisis automático inexistente. Lo que ocurre es una
              subida: 📤. */}
          <View style={[styles.cameraPreview, isScanning && styles.cameraScanning]}>
            {!isScanning ? (
              <Text style={{fontSize: 64}}>👤</Text>
            ) : (
              <Text style={{fontSize: 64}}>{step >= 2 ? '✅' : '📤'}</Text>
            )}
          </View>
          
          <View style={styles.instructionBox}>
            <Text style={styles.instructionText}>
              {instructions[step]}
            </Text>
            {isScanning && step < 2 && (
              <ActivityIndicator color={Colors.accent} style={{marginTop: 12}} />
            )}
          </View>
        </View>

        {!isScanning && (
          <Button 
            title="Abrir Cámara" 
            onPress={handleStartScan} 
          />
        )}
      </View>
      </SafeAreaView>
    </ScreenBackground>
  );
}

const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: 'rgba(10, 37, 64, 0.85)' },
  container: { flex: 1, padding: SIZES.padding, justifyContent: 'space-between', paddingBottom: 40 },
  header: { marginTop: 20 },
  title: { fontSize: 26, fontWeight: 'bold', color: Colors.white, marginBottom: 8, textAlign: 'center' },
  subtitle: { fontSize: SIZES.font, color: 'rgba(255,255,255,0.8)', textAlign: 'center', lineHeight: 22 },
  previewContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  cameraPreview: { width: 250, height: 350, borderRadius: 125, backgroundColor: Colors.white, justifyContent: 'center', alignItems: 'center', borderWidth: 4, borderColor: 'transparent', overflow: 'hidden' },
  cameraScanning: { borderColor: Colors.accent },
  instructionBox: { marginTop: 40, backgroundColor: 'rgba(255,255,255,0.1)', padding: 20, borderRadius: SIZES.radius, width: '100%', alignItems: 'center' },
  instructionText: { fontSize: SIZES.medium, color: Colors.white, fontWeight: '600', textAlign: 'center' },
  backButton: { padding: 8, alignSelf: 'flex-start', marginLeft: 20, marginTop: Platform.OS === 'ios' ? 10 : 20 },
  
  // Estilos para la vista de cámara en vivo
  cameraScreen: { flex: 1, backgroundColor: '#000' },
  cameraOverlay: {
    ...StyleSheet.absoluteFill as any,
    justifyContent: 'center',
    alignItems: 'center',
  },
  ovalCutout: {
    width: 300,
    height: 400,
    borderRadius: 200,
    borderWidth: Math.max(width, height), // Suficientemente grande para cubrir todo el fondo
    borderColor: 'rgba(10, 37, 64, 0.90)', 
  },
  cameraOverlayContent: {
    ...StyleSheet.absoluteFill as any,
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 60,
    paddingBottom: 40,
  },
  cameraInstructionText: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#FFF',
    textAlign: 'center',
    marginTop: 40,
    textShadowColor: 'rgba(0, 0, 0, 0.75)',
    textShadowOffset: { width: -1, height: 1 },
    textShadowRadius: 10,
  },
  cameraControls: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingBottom: 20,
  },
  shutterButton: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
  },
  shutterButtonInner: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: '#FFF',
  },
  // Mientras se captura y se ajusta la foto el obturador se ve apagado: la
  // señal de que la app está trabajando y no de que el botón está muerto.
  shutterButtonDisabled: {
    backgroundColor: 'rgba(255, 255, 255, 0.12)',
  },
  closeCameraButton: {
    padding: 15,
  },
  closeCameraText: {
    color: '#FFF',
    fontSize: 16,
    fontWeight: '600',
  }
});
